/* eslint-disable import/no-cycle */
import React from "react";

import moment from "moment";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import CurrencyFormat from "react-currency-format";

import ButtonComponent from "../button";

import { InvoiceModel } from "../../../domain/models/invoice";

import { theme } from "../../styles/theme";

import * as S from "./styled";

import LucralizeLogo from "../../assets/images/logo.png";

type PageProps = {
  invoice: InvoiceModel | any | undefined;
  onCancelCallback?: () => void;
};

const toCep = (cep: string): string => {
  if (!cep) return "";

  if (cep.length === 8) {
    return `${cep.substr(0, 5)}-${cep.substr(5, 3)}`;
  }
  return cep;
};

const toCnpj = (cnpj: string): string => {
  if (!cnpj) return "";

  if (cnpj.length === 14) {
    return `${cnpj.substr(0, 2)}.${cnpj.substr(2, 3)}.${cnpj.substr(
      5,
      3,
    )}/${cnpj.substr(8, 4)}-${cnpj.substr(12, 2)}`;
  }
  return cnpj;
};

const DocComponent: React.FC<any> = ({ invoice }) => (
  <div
    id="divToPrint"
    style={{
      backgroundColor: theme.colors.darkBlue,
      fontFamily: theme.font.poppins,
      maxWidth: "210mm",
      minHeight: "150mm",
      marginLeft: "auto",
      marginRight: "auto",
      padding: "15px",
      margin: "0 auto",
    }}
  >
    <S.HeaderStyled>
      <div>
        <span
          style={{
            fontSize: "9px",
          }}
        >
          {moment().format("DD/MM/YYYY")}
        </span>
      </div>
      <img src={LucralizeLogo} alt="" />
    </S.HeaderStyled>
    <div>
      <S.Header>
        <S.CompanyInfo>
          <span className="company-name">
            {invoice?.developerCompany?.socialReason?.toUpperCase()}
          </span>
          <span className="company-number">
            <span>CNPJ: </span>
            {toCnpj(invoice?.developerCompany?.subscriptionNumber || "")}
          </span>
        </S.CompanyInfo>
        <S.InvoiceHeaderInfo>
          <span className="invoice-number">Invoice #{invoice?.id || ""}</span>
          <span className="invoice-date">
            <span>Creation date: </span>
            {moment(invoice?.issuedOn).format("DD-MM-YYYY")}
          </span>
          <span className="invoice-date">
            <span>Due Date: </span>
            {moment(invoice?.date).format("DD-MM-YYYY")}
          </span>
        </S.InvoiceHeaderInfo>
      </S.Header>
      <S.Divisor />
      <S.BillInfo>
        <div>
          <span>Bill from:</span>
          <span>{invoice?.developerCompany?.socialReason?.toUpperCase()}</span>
          <div className="company-address">
            {invoice?.developerCompany?.address?.place},{" "}
            {invoice?.developerCompany?.address?.district}.{" "}
            {invoice?.developerCompany?.address?.city} -{" "}
            {invoice?.developerCompany?.address?.state}, Brazil. Zip Code:{" "}
            {toCep(invoice?.developerCompany?.address?.zipCode)}
          </div>
        </div>
        <div>
          <span>Bill to:</span>
          <span>{invoice?.companyName}</span>
          <div className="company-address">
            {invoice?.address || invoice?.addressName}, {invoice?.city} -{" "}
            {invoice?.state}, {invoice?.nation}.
          </div>
        </div>
      </S.BillInfo>
      <S.Title>
        <span>Services</span> <span>Amount</span>
      </S.Title>
      <S.Divisor />
      <S.ServiceDescription>
        <S.ServiceItem>
          {invoice?.description || invoice?.serviceDescription}
        </S.ServiceItem>
        <S.ServiceItem>
          <CurrencyFormat
            value={invoice?.serviceValue
              ?.replaceAll(".", "")
              .replaceAll(",", ".")}
            thousandSeparator={false}
            prefix={invoice?.currency?.split(" - ")[2]}
            displayType="text"
          />
        </S.ServiceItem>
      </S.ServiceDescription>
      <S.Divisor />
      <S.Amount>
        Total{" "}
        <span>
          <CurrencyFormat
            value={invoice?.serviceValue
              ?.replaceAll(".", "")
              .replaceAll(",", ".")}
            thousandSeparator={false}
            prefix={invoice?.currency?.split(" - ")[2]}
            displayType="text"
          />
        </span>
      </S.Amount>
      <S.PaymentInfo>
        <span className="payment-info-title">
          Pay to banking details below:
        </span>
        <div>
          <span>Bank:</span> {invoice?.bankName}
        </div>
        <div>
          <span>Bank Agency:</span> {invoice?.bankAgency}
        </div>
        <div>
          <span>Bank Account:</span> {invoice?.bankAccount}
        </div>
        <div>
          <span>Digit:</span> {invoice?.bankDigit}
        </div>
      </S.PaymentInfo>
    </div>
  </div>
);

const TemplateInvoice = ({
  invoice,
  onCancelCallback,
}: PageProps): JSX.Element => {
  const printDocument = (): void => {
    const input: any = document.getElementById("divToPrint");
    html2canvas(input).then((canvas) => {
      const imgData: any = canvas.toDataURL("image/png");
      // eslint-disable-next-line new-cap
      const pdf: any = new jsPDF("p", "px", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "JPEG", 0, 0, width, height);
      pdf.save(`invoice-${moment().format("DD-MM-YYYY")}.pdf`);
    });
  };

  return (
    <S.Wrapper>
      <DocComponent invoice={invoice} />
      <div className="d-flex justify-content-center mt-4">
        <div className="ms-3">
          <ButtonComponent
            title="Cancelar"
            backgroundColor={theme.colors.darkBlue}
            color={theme.colors.white}
            onClick={onCancelCallback}
          />
        </div>
        <div className="me-3">
          <ButtonComponent
            title="Download"
            backgroundColor={theme.colors.blue400}
            color={theme.colors.darkBlue}
            onClick={async () => {
              printDocument();
            }}
          />
        </div>
      </div>
    </S.Wrapper>
  );
};
export default TemplateInvoice;
